import { usePermissao } from "@/contexts/autorizacao/permissao";
import { FiltroAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/components/FiltroAnaliseLaboratorial/FiltroAnaliseLaboratorial";
import { ItemListFiltroRequisicao } from "@/contexts/laboratorio/analiseLaboratorial/components/ItemListFiltroRequisicao/ItemListFiltroRequisicao";
import { ItemListRequisicao } from "@/contexts/laboratorio/analiseLaboratorial/components/ItemListRequisicao/ItemListRequisicao";
import { SubmitBackupRequisicao } from "@/contexts/laboratorio/analiseLaboratorial/components/SubmitBackupRequisicao";
import { useAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/hooks/useAnaliseLaboratorial";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Col,
  Divider,
  Grid,
  List,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

const PAGE_SIZE = 20;
export function ListaRequisicoesAnaliseLaboratorial() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message, notification } = App.useApp();
  const page = searchParams.get("page") || "1";
  const { listaRequisicoes, getAnalisesLaboratoriaisApi } =
    useAnaliseLaboratorial();
  const { hasEnabledPermissao } = usePermissao();

  const currentPage = Number(page);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedRequisicoes = listaRequisicoes.slice(startIndex, endIndex);

  const {
    data: listaRequisicoesFiltro,
    isLoading: isLoadingFiltro,
    refetch: refetchRequisicoesFiltro,
    error: errorRequisicoesFiltro,
  } = useQuery({
    queryKey: ["REQUISICOES_FILTRO", searchParams.toString()],
    enabled:
      isOnline &&
      searchParams.get("filtro") === "true" &&
      !!searchParams.get("estabelecimentoId"),

    queryFn: async () => {
      const response = await getAnalisesLaboratoriaisApi({
        estabelecimentoId: Number(searchParams.get("estabelecimentoId")),
        dataColeta: searchParams.get("dataColeta") || undefined,
        codigo: searchParams.get("codigo") || undefined,
        page: currentPage,
      });
      return response;
    },
  });

  const redirectToNovaRequisicao = () => {
    navigate(
      `/analises-laboratoriais/requisicoes/novo?${searchParams.toString()}`
    );
  };

  useEffect(() => {
    document.title = "Inspeção Mobile - Requisições de Análise Laboratorial";

    if (!isOnline && searchParams.get("filtro") === "true") {
      searchParams.delete("filtro");
      setSearchParams(searchParams);
    }

    if (errorRequisicoesFiltro) {
      const messageError = formatMessageError(errorRequisicoesFiltro);
      notification.error({
        message: "Não foi possivel carregar a lista de requisiçoes.",
        description: messageError,
        key: "error-filtro",
      });
    }
  }, [errorRequisicoesFiltro]);

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Requisições de Análise Laboratorial
        </Typography.Title>
      </Space>

      <Row gutter={16} justify="space-between" align="middle">
        <Col>
          <Row gutter={8} align="middle">
            <Col>
              <Tooltip title="Filtro" destroyOnHidden>
                <Button
                  onClick={() => {
                    if (searchParams.get("filtro") === "true") {
                      searchParams.delete("filtro");
                      searchParams.delete("estabelecimentoId");
                      searchParams.delete("dataColeta");
                      searchParams.delete("codigo");
                      searchParams.delete("page");
                      setSearchParams(searchParams);
                      message.info({
                        content:
                          "Filtro limpo, exibindo lista de requisições sincronizadas.",
                        key: "filtro",
                      });
                    } else {
                      searchParams.set("filtro", "true");
                    }
                    setSearchParams(searchParams);
                  }}
                  danger={searchParams.get("filtro") === "true"}
                >
                  <FilterOutlined />
                  {searchParams.get("filtro") === "true" && " Fechar"}
                </Button>
              </Tooltip>
            </Col>

            <Col>
              <Tooltip title="Nova Requisição de Análise">
                <Button
                  type="primary"
                  onClick={redirectToNovaRequisicao}
                  disabled={!hasEnabledPermissao("RAL_CRIAR")}
                >
                  <PlusOutlined />
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </Col>

        <Col>
          <SubmitBackupRequisicao />
        </Col>
      </Row>

      {searchParams.get("filtro") === "true" && (
        <Row>
          <Col span={24} style={{ marginTop: 16 }}>
            <FiltroAnaliseLaboratorial />
          </Col>
        </Row>
      )}

      {searchParams.get("filtro") === "true" && (
        <>
          <Divider>
            <Typography.Text strong>Resultados da busca</Typography.Text>
          </Divider>
          <List
            size="small"
            bordered
            dataSource={listaRequisicoesFiltro?.content || []}
            renderItem={(item, index) => (
              <ItemListFiltroRequisicao
                key={index}
                item={item}
                reloadItems={refetchRequisicoesFiltro}
              />
            )}
            loading={isLoadingFiltro}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaRequisicoesFiltro?.totalElements || 0,
              showSizeChanger: false,
              onChange: (page) => {
                searchParams.set("page", String(page));
                setSearchParams(searchParams);
              },
              align: "end",
              size: "small",
            }}
          />
        </>
      )}

      {!searchParams.get("filtro") && (
        <>
          <Divider>
            <Typography.Text strong>Requisições sincronizadas</Typography.Text>
          </Divider>

          <List
            size="small"
            bordered
            dataSource={paginatedRequisicoes}
            renderItem={(item, index) => (
              <ItemListRequisicao key={index} item={item} />
            )}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaRequisicoes.length,
              onChange: (page) => setSearchParams({ page: String(page) }),
              showSizeChanger: false,
              align: "end",
              size: "small",
            }}
          />
        </>
      )}
    </>
  );
}
